import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import TrendCard from "../components/TrendCard";
import Avatar from "../components/Avatar";
import QuickTour from "../components/QuickTour";
import useMarketSocket from "../hooks/useMarketSocket";
import { getTrendIndexes } from "../lib/api";
import { TrendingUp, TrendingDown, Search, HelpCircle, Radio } from "lucide-react";

const SORTS = [
  { v: "form", l: "Top Form" },
  { v: "gainers", l: "On Fire" },
  { v: "losers", l: "Slumping" },
];

const CATS = ["all", "bollywood", "reality", "creator", "music", "athletes"];

export default function Market() {
  const [indexes, setIndexes] = useState([]);
  const [sort, setSort] = useState("form");
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [pulsed, setPulsed] = useState({});
  const [tourOpen, setTourOpen] = useState(false);
  const { prices, connected } = useMarketSocket();

  useEffect(() => {
    getTrendIndexes({ sort, category: cat }).then(setIndexes);
  }, [sort, cat]);

  // Apply live ticks
  useEffect(() => {
    if (!Object.keys(prices).length) return;
    setIndexes((prev) => prev.map(x => {
      const u = prices[x.index_id];
      if (!u) return x;
      return { ...x, current_price: u.current_price, change_24h: u.change_24h, heat_score: u.heat_score };
    }));
    const ids = Object.keys(prices);
    setPulsed(Object.fromEntries(ids.map(id => [id, true])));
    const t = setTimeout(() => setPulsed({}), 400);
    return () => clearTimeout(t);
  }, [prices]);

  useEffect(() => {
    try {
      if (!localStorage.getItem("fanpuff_tour_seen")) setTourOpen(true);
    } catch (e) { console.debug("[market] tour-seen localStorage unavailable", e); }
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return indexes;
    return indexes.filter(x => x.name.toLowerCase().includes(term) || x.handle.toLowerCase().includes(term));
  }, [q, indexes]);

  const topG = [...indexes].sort((a, b) => b.change_24h - a.change_24h).slice(0, 3);
  const topL = [...indexes].sort((a, b) => a.change_24h - b.change_24h).slice(0, 3);

  return (
    <div className="min-h-screen pb-safe" data-testid="heat-page">
      <Header title="Top Form" />
      <div className="max-w-xl mx-auto px-4 pt-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold">Top Form</h1>
            <p className="text-sm text-white/60 mt-1">
              Live form points for 237+ stars. Scout who's <b className="text-[#00FF66]">peaking</b> &amp; who's <b className="text-[#FF0055]">cooling</b> before you build your Circle.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className="chip" style={{ background: connected ? "#00FF6622" : "#FFD60022", color: connected ? "#00FF66" : "#FFD600", border: `1px solid ${connected ? "#00FF6655" : "#FFD60055"}` }}>
              <Radio className="w-3 h-3" /> {connected ? "Live" : "Connecting"}
            </span>
            <button
              className="chip hover:bg-white/10"
              style={{ background: "rgba(255,255,255,0.05)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }}
              onClick={() => setTourOpen(true)}
              data-testid="market-help"
            >
              <HelpCircle className="w-3 h-3" /> New? Tour
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <MoverBox title="Top Gainers" icon={TrendingUp} color="#00FF66" items={topG} />
          <MoverBox title="Top Losers" icon={TrendingDown} color="#FF0055" items={topL} />
        </div>

        <div className="relative">
          <Search className="w-4 h-4 absolute top-3.5 left-3 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search stars…"
            data-testid="market-search"
            className="w-full rounded-full bg-white/5 border border-white/10 pl-9 pr-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[#B026FF]/50"
          />
        </div>

        <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
          {SORTS.map(s => (
            <button
              key={s.v}
              data-testid={`market-sort-${s.v}`}
              onClick={() => setSort(s.v)}
              className="chip whitespace-nowrap px-3 py-1.5 text-xs"
              style={sort === s.v
                ? { background: "linear-gradient(90deg,#B026FF,#00F0FF)", color: "#000", border: "1px solid transparent" }
                : { background: "rgba(255,255,255,0.05)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }}
            >{s.l}</button>
          ))}
        </div>
        <div className="flex gap-2 overflow-x-auto -mx-1 px-1 pb-1">
          {CATS.map(c => (
            <button
              key={c}
              data-testid={`market-cat-${c}`}
              onClick={() => setCat(c)}
              className="chip whitespace-nowrap px-3 py-1.5 text-xs"
              style={cat === c
                ? { background: "#00F0FF22", color: "#00F0FF", border: "1px solid #00F0FF55" }
                : { background: "rgba(255,255,255,0.05)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }}
            >{c}</button>
          ))}
        </div>

        <div className="space-y-3">
          {filtered.map((idx) => (
            <Link key={idx.index_id} to={`/market/${idx.index_id}`} className="block" data-testid={`market-row-${idx.handle}`}>
              <TrendCard index={idx} livePulse={pulsed[idx.index_id]} />
            </Link>
          ))}
          {filtered.length === 0 && <p className="text-white/50 text-sm">No stars match.</p>}
        </div>

        <Link
          to="/requests"
          data-testid="market-request-cta"
          className="block glass-strong rounded-2xl p-4 card-hover"
        >
          <p className="text-xs uppercase tracking-widest font-bold text-white/50">Don't see your fav?</p>
          <p className="font-display text-lg font-bold mt-1">Request an influencer →</p>
          <p className="text-xs text-white/60 mt-1">Get fans to upvote. Highest-voted gets added to Fanpuff first.</p>
        </Link>

        <div className="h-10" />
      </div>
      <BottomNav />
      <QuickTour open={tourOpen} onClose={() => setTourOpen(false)} />
    </div>
  );
}

function MoverBox({ title, icon: Icon, color, items }) {
  return (
    <div className="glass rounded-2xl p-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest font-bold" style={{ color }}>
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      <div className="mt-2 space-y-1.5">
        {items.map((x) => (
          <div key={x.index_id} className="flex items-center gap-2">
            <Avatar name={x.name} category={x.category} size={24} rounded={6} showSymbol={false} />
            <span className="text-xs truncate flex-1">{x.name}</span>
            <span className="text-xs font-mono-brand" style={{ color }}>
              {x.change_24h > 0 ? "+" : ""}{x.change_24h?.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
